import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { customerApi } from '../api/customers';
import { googleSignIn } from '../api/googleSignIn';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { SESSION_ENDED } from '../components/SessionListener';
import { getCustomerSession, isCustomerMarkedSignedIn, startCustomerSession } from '../lib/customerSession';
import { isDriverSignedIn } from '../lib/driverSession';
import { refusalMessage } from '../api/errors';
import InGoLogo from '../components/InGoLogo';
import { LOGIN_HERO_ART, LOGIN_HERO_ICONS } from '../lib/ingoLogo';
import './auth.css';

function IconEnvelope() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 6h16v12H4V6Zm0 0 8 7 8-7"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconLock() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M8 11V8a4 4 0 0 1 8 0v3"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconEye({ open }) {
  if (open) {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.7" />
      </svg>
    );
  }
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M3 3l18 18M10.5 10.5a3 3 0 0 0 3 3M5.2 5.2C3.1 6.4 1.5 8.1 1 9.5c0 0 4 7 11 7 1.2 0 2.3-.2 3.3-.5M8.2 4.2C9.3 3.8 10.6 3.5 12 3.5c7 0 11 6.5 11 6.5-.2.4-1.1 1.6-2.4 2.8"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function LoginPage({ google = googleSignIn, customers = customerApi }) {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  // Google's token, kept while the server asks for the password once before linking it.
  const [pendingGoogle, setPendingGoogle] = useState(null);
  // Back to the page the sign-in gate sent them from (CustomerAuthGate), else home.
  const from = typeof state?.from === 'string' && state.from.startsWith('/') && !state.from.startsWith('//') ? state.from : '/home';

  /**
   * "Continue with Google" answered: a client is signed in (made at once when
   * new, ADR 0023 §5). One imported from the old app chooses a password first.
   */
  const googleSignedIn = (result) => {
    if (result.status !== 'signed_in') return;
    startCustomerSession(result.account, { rememberMe });
    navigate(result.mustChangePassword ? '/choose-password' : from, { replace: true });
  };

  /** An account has this address, not proved here: the server's sentence, and Google linked after the password. */
  const googleRefused = (error, idToken) => {
    if (error?.code === 'EMAIL_NOT_VERIFIED_HERE') setPendingGoogle(idToken);
    setErrorMessage(refusalMessage(error));
  };

  if (isDriverSignedIn()) {
    return <Navigate to="/driver/home" replace />;
  }
  if (isCustomerMarkedSignedIn() && getCustomerSession()) {
    return <Navigate to="/home" replace />;
  }

  const goHome = () => {
    navigate(from, { replace: true });
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    if (!email.trim() || !password) {
      setErrorMessage('Please enter email and password.');
      return;
    }

    setIsSubmitting(true);
    try {
      // InGo's API checks the password against its hash (M3); the old screen
      // read every client's password into the phone to compare it here.
      const { account, mustChangePassword } = await customers.signIn(email.trim().toLowerCase(), password);
      startCustomerSession(account, { rememberMe });
      // Google was refused until the password proved the address: link it now (ADR 0023 §5).
      if (pendingGoogle) await google.link('customer', pendingGoogle).catch(() => {});
      // A password carried over from the old app is replaced before anything
      // else (D15). Never the password itself: navigation state lives in the history.
      if (mustChangePassword) {
        navigate('/choose-password', { replace: true });
        return;
      }
      goHome();
    } catch (error) {
      setErrorMessage(refusalMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-page auth-page--login">
      <header className="auth-login-hero">
        <div className="auth-login-hero-waves" aria-hidden />
        <div className="auth-login-hero-top">
          <h1 className="auth-login-hero-logo-wrap">
            <InGoLogo variant="hero" />
          </h1>
        </div>
        <div className="auth-login-hero-scene" aria-hidden>
          <img
            src={LOGIN_HERO_ART || LOGIN_HERO_ICONS[0]}
            alt=""
            className="auth-login-hero-art"
            decoding="async"
          />
        </div>
      </header>

      <main className="auth-login-body">
        <div className="auth-login-card">
          <form className="auth-login-form" onSubmit={handleLoginSubmit} noValidate>
            <div className="auth-login-heading">
              <h2 className="auth-login-title">Welcome Back!</h2>
            </div>
            {state?.sessionEnded ? (
              <p className="auth-message auth-message--success" role="status">
                {SESSION_ENDED}
              </p>
            ) : null}
            {state?.passwordReset ? (
              <p className="auth-message auth-message--success" role="status">
                Your password was updated. Sign in with your new password.
              </p>
            ) : null}
            {/* "Delete my account" asked: the server's sentence names the day (ADR 0038). */}
            {typeof state?.deletionScheduled === 'string' ? (
              <p className="auth-message auth-message--success" role="status">
                {state.deletionScheduled}
              </p>
            ) : null}

            <div className="auth-field">
              <label className="auth-login-label" htmlFor="login-email">
                Email Address
              </label>
              <div className="auth-login-input-wrap">
                <span className="auth-login-iconbox" aria-hidden>
                  <IconEnvelope />
                </span>
                <input
                  id="login-email"
                  className="auth-login-input"
                  type="email"
                  name="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div className="auth-field">
              <label className="auth-login-label" htmlFor="login-password">
                Password
              </label>
              <div className="auth-login-input-wrap">
                <span className="auth-login-iconbox" aria-hidden>
                  <IconLock />
                </span>
                <input
                  id="login-password"
                  className="auth-login-input"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="auth-login-input-toggle"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <IconEye open={!showPassword} />
                </button>
              </div>
            </div>

            <div className="auth-options-row auth-login-options">
              <label className="auth-login-remember">
                <input
                  type="checkbox"
                  className="auth-login-checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span className="auth-login-checkbox-box" aria-hidden />
                <span>Remember me</span>
              </label>
              <Link to="/forgot-password?realm=customer" className="auth-login-forgot">
                Forgot password?
              </Link>
            </div>

            <button type="submit" className="auth-login-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Logging in...' : 'Login'}
            </button>
            {errorMessage ? <p className="auth-message auth-message--error">{errorMessage}</p> : null}
          </form>

          <div className="auth-login-or" aria-hidden>
            <span className="auth-login-or-line" />
            <span className="auth-login-or-text">OR</span>
            <span className="auth-login-or-line" />
          </div>

          <GoogleSignInButton realm="customer" google={google} onResult={googleSignedIn} onError={googleRefused} />

          <p className="auth-login-register-line">
            Don&apos;t have an account?{' '}
            <Link to="/register" className="auth-login-register-link">
              Register
            </Link>
          </p>

          <p className="auth-login-register-line">
            Are you a driver?{' '}
            <Link to="/driver/login" className="auth-login-register-link">
              Driver login
            </Link>
          </p>
        </div>
      </main>

      <footer className="auth-login-footer">
        <p className="auth-login-copyright">
          &copy; {new Date().getFullYear()} InGo. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
